import React, { useState } from "react";
import { motion } from "framer-motion";
import { Globe, FileCheck2, BadgeCheck, CalendarDays } from "lucide-react";

const HIGHLIGHTS = [
  {
    icon: Globe,
    num: "01",
    stat: "80+",
    title: "Nationalités",
    desc: "Rencontrez, côtoyez et échangez avec des intervenants et des étudiants du monde entier.",
  },
  {
    icon: FileCheck2,
    num: "02",
    stat: "Admission",
    title: "Concours & entretien",
    desc: "Sur concours et entretien à partir de post-bac (admission parallèle de Bac+2 à Bac+5).",
  },
  {
    icon: BadgeCheck,
    num: "03",
    stat: "Français",
    title: "Diplôme",
    desc: "Vous obtenez un diplôme français reconnu internationalement, délivré par l'École de Commerce de Lyon.",
  },
  {
    icon: CalendarDays,
    num: "04",
    stat: "4",
    title: "Rentrées par an",
    desc: "Janvier · Avril · Juillet · Octobre — choisissez le moment qui correspond à votre parcours.",
    chips: ["Janvier", "Avril", "Juillet", "Octobre"],
  },
];

const Card = ({ item, index }) => {
  const [hovered, setHovered] = useState(false);
  const Icon = item.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ y: -8 }}
      data-testid={`highlight-${index}`}
      className="relative group bg-white border border-navy-100 overflow-hidden cursor-default isolate rounded-2xl"
      style={{
        boxShadow: hovered
          ? "0 30px 70px -20px rgba(0, 35, 98, 0.30), 0 0 0 1px rgba(237, 41, 57, 0.10)"
          : "0 8px 28px -16px rgba(0, 35, 98, 0.12)",
      }}
    >
      {/* Animated background gradient */}
      <div
        className={`absolute inset-0 bg-gradient-to-br from-navy-500 via-navy-700 to-[#000e27] origin-bottom transition-transform duration-[700ms] ease-[cubic-bezier(0.65,0,0.35,1)] -z-10 ${
          hovered ? "scale-y-100" : "scale-y-0"
        }`}
      />

      {/* Background pattern dots */}
      <svg
        className={`absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-700 ${
          hovered ? "opacity-[0.08]" : "opacity-0"
        }`}
        preserveAspectRatio="none"
      >
        <defs>
          <pattern id={`hl-pat-${index}`} width="16" height="16" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="white" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#hl-pat-${index})`} />
      </svg>

      {/* Glow orb */}
      <div
        className={`absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl transition-opacity duration-700 ${
          hovered ? "opacity-100 bg-red-500/30" : "opacity-0"
        }`}
      />

      {/* Giant background number */}
      <span
        className={`absolute -top-2 right-4 font-display text-[170px] md:text-[200px] leading-none select-none pointer-events-none transition-all duration-500 ${
          hovered ? "text-white/[0.08] scale-110" : "text-navy-700/[0.06] scale-100"
        }`}
        aria-hidden
      >
        {item.num}
      </span>

      <div className="relative p-8 md:p-10 lg:p-12 flex flex-col gap-6 min-h-[380px] items-center text-center">
        {/* Tilted icon */}
        <motion.div
          animate={{ rotate: hovered ? -8 : -4, scale: hovered ? 1.05 : 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-16 h-16 mx-auto"
        >
          <div
            className={`absolute inset-0 transition-colors duration-500 ${
              hovered ? "bg-red-500" : "bg-navy-50"
            }`}
            style={{ borderRadius: "8px" }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon
              className={`h-7 w-7 transition-colors duration-500 ${
                hovered ? "text-white" : "text-navy-500"
              }`}
              strokeWidth={1.5}
            />
          </div>
        </motion.div>

        <div className="flex-1">
          <div className={`stat-num text-4xl sm:text-5xl text-navy-700 mb-2 leading-none transition-colors duration-500 ${hovered ? "text-white" : "text-navy-700"}`}>
            {item.stat}
          </div>
          <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold mb-2 transition-colors duration-500 ${hovered ? "text-red-400" : "text-red-500"}`}>
            {item.title}
          </div>
          <p className={`text-[14px] leading-relaxed transition-colors duration-500 ${hovered ? "text-white/80" : "text-[#555]"}`}>
            {item.desc}
          </p>

          {item.chips && (
            <div className="flex flex-wrap gap-1.5 mt-5 pt-4 border-t border-navy-100">
              {item.chips.map((c) => (
                <span
                  key={c}
                  className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] border transition-colors duration-500 ${
                    hovered ? "border-white/20 text-white/80 bg-white/10" : "border-navy-100 text-navy-700 bg-[#FAF8F3]"
                  }`}
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tricolor stripe */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] flex overflow-hidden">
        <motion.span animate={{ scaleX: hovered ? 1 : 0 }} transition={{ duration: 0.5, ease: [0.65, 0, 0.35, 1], delay: hovered ? 0 : 0 }} className="flex-1 bg-[#002362] origin-left" />
        <motion.span animate={{ scaleX: hovered ? 1 : 0 }} transition={{ duration: 0.5, ease: [0.65, 0, 0.35, 1], delay: hovered ? 0.1 : 0 }} className="flex-1 bg-white origin-left" />
        <motion.span animate={{ scaleX: hovered ? 1 : 0 }} transition={{ duration: 0.5, ease: [0.65, 0, 0.35, 1], delay: hovered ? 0.2 : 0 }} className="flex-1 bg-[#ED2939] origin-left" />
      </div>
    </motion.div>
  );
};

export const Highlights = () => {
  return (
    <section id="highlights" data-testid="highlights-section" className="section-padding bg-white">
      <div className="container-edge">
        <div className="max-w-3xl mb-14">
          <div className="overline mb-4">L'essentiel</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-5xl text-navy-700 leading-tight">
            Quatre raisons d'<em className="not-italic red-shine">avancer</em>.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {HIGHLIGHTS.map((h, i) => (
            <Card key={h.title} item={h} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Highlights;
